/**
 * SOLID Review
 * S — Strong. One concern: catch what goes wrong in this browser — a window error, an unhandled rejection,
 *     a render a boundary caught, a failed port call — and hand it to the Application, once.
 * O — Good. A new source is one more method calling `report`.
 * L — Strong. The observer it makes is a drop-in `IPortObserver` around the one it wraps.
 * I — Good. Four entry points, one per source, and `bind`.
 * D — Strong. The kernel's observer type and the `ClientUseCases` port's one op, bound after assembly.
 */
import type { IPortObserver, IPayloadCallContext } from '@ml3d/kernel';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';
import type { IContextRequest } from '../../application/ports/IContextRequest';

type TReport = Omit<TClientUseCaseInput<'reportClientError'>, 'now'>;

/** The longest text taken from an error before the Application and the wire bound it further. */
const TEXT_MAX = 4_000;
/** However the caps further in behave, this object hands over no more than this many reports per page load. */
const FUSE = 100;
/** Port failures that are bugs (D7): a broken contract, a provider that threw. Network weather is not. */
const REPORTED_CODES: readonly string[] = Object.freeze(['CONTRACT', 'INTERNAL', 'THROWN']);
/** The reporting path itself: a failure there is never reported, or one bad report would beget the next. */
const OWN_PORTS: readonly string[] = Object.freeze(['ClientDiagnosticsServer', 'RestClientErrorsApi']);
const OWN_OPS: readonly string[] = Object.freeze(['ClientUseCases.reportClientError', 'ClientDecisions.planClientError']);

export interface IConfigDepClientErrorReporting {
  readonly now: () => number;
  readonly context: () => IContextRequest;
}

/** What a window's error listeners need of it. */
export interface IServiceErrorTarget {
  addEventListener(type: 'error' | 'unhandledrejection', listener: (event: Event) => void): void;
}

function textOf(value: unknown): string {
  return String(value).slice(0, TEXT_MAX);
}

/**
 * CLIENT ERROR REPORTING'S FRAMEWORK HALF (plan 2026-10-09, EP7). Built before the graph — the port observer
 * it makes is handed to `FrontendConnectors` — and bound to the assembled `ClientUseCases` socket after.
 * Anything caught before `bind` is dropped: the boot guard in index.html covers that stretch (EP8).
 *
 * ⚠ IT NEVER THROWS AND NEVER RE-ENTERS. A report is fire-and-forget; while one is in flight, a port failure
 * the observer sees is not reported, because it may be the report's own (the state's apply, the decision).
 */
export class ClientErrorReporting {
  private useCases: Pick<IPortClientUseCases, 'reportClientError'> | null = null;
  private handedOver = 0;
  private inFlight = 0;

  constructor(private readonly deps: IConfigDepClientErrorReporting) {}

  bind(useCases: Pick<IPortClientUseCases, 'reportClientError'>): void {
    this.useCases = useCases;
  }

  report(report: TReport): void {
    const useCases = this.useCases;
    if (useCases === null || this.handedOver >= FUSE) return;
    this.handedOver += 1;
    this.inFlight += 1;
    const command: TClientUseCaseInput<'reportClientError'> = { ...report, now: this.deps.now() };
    void Promise.resolve()
      .then(() => useCases.reportClientError(this.deps.context(), command))
      .catch(() => undefined)
      .finally(() => { this.inFlight -= 1; });
  }

  /** A JavaScript value something threw, as a report of `kind`. */
  thrown(kind: 'UNCAUGHT' | 'REJECTION' | 'RENDER', value: unknown, extraStack: string | null = null): void {
    const error = value instanceof Error ? value : null;
    const stack = [error?.stack ?? null, extraStack].filter((s): s is string => typeof s === 'string' && s !== '').join('\n');
    const report: TReport = {
      kind, message: textOf(error === null ? value : error.message), stack: stack === '' ? null : stack.slice(0, TEXT_MAX), port: null, op: null, code: null,
    };
    this.report(report);
  }

  /**
   * The window's two listeners. A cross-origin script's error arrives as "Script error." with no detail —
   * the browser hides it — and so does one whose file is on another origin: both are THIRD_PARTY (an ad,
   * the consent platform), counted and never described.
   */
  install(target: IServiceErrorTarget, origin: string): void {
    target.addEventListener('error', (event) => {
      const e = event as ErrorEvent;
      const foreign = e.message === 'Script error.' || (typeof e.filename === 'string' && e.filename !== '' && !e.filename.startsWith(origin));
      if (foreign) {
        const third: TReport = { kind: 'THIRD_PARTY', message: '', stack: null, port: null, op: null, code: null };
        this.report(third);
        return;
      }
      this.thrown('UNCAUGHT', e.error ?? e.message);
    });
    target.addEventListener('unhandledrejection', (event) => {
      this.thrown('REJECTION', (event as PromiseRejectionEvent).reason);
    });
  }

  /** React's `onCaughtError`: a render an error boundary caught. The boundary still shows its fallback. */
  readonly renderError = (error: unknown, info: { readonly componentStack?: string }): void => {
    this.thrown('RENDER', error, info.componentStack ?? null);
  };

  /** The kernel's observer, wrapped: every failed seam it sees that is a bug is also a PORT report. */
  observer(inner: IPortObserver): IPortObserver {
    const reporting = this;
    const wrapped: IPortObserver = {
      onCall: (port: string, op: string, ctx: IPayloadCallContext | null) => inner.onCall(port, op, ctx),
      onResult(port: string, op: string, ctx: IPayloadCallContext | null, ok: boolean, elapsedMs: number, handle?: unknown, code?: string): void {
        inner.onResult(port, op, ctx, ok, elapsedMs, handle, code);
        const reason = code ?? 'THROWN';
        if (ok || !REPORTED_CODES.includes(reason) || reporting.inFlight > 0) return;
        if (OWN_PORTS.includes(port) || OWN_OPS.includes(`${port}.${op}`)) return;
        const failed: TReport = { kind: 'PORT', message: `${port}.${op} failed: ${reason}`, stack: null, port, op, code: reason };
        reporting.report(failed);
      },
    };
    return wrapped;
  }
}
